import React, { useEffect, useMemo, useState } from 'react';
import { FaSave, FaRedoAlt } from 'react-icons/fa';
import { PAGE_DEFINITIONS } from '../constants/accessControl';

const IndstillingerAdgang = () => {
  const [pages, setPages] = useState(PAGE_DEFINITIONS);
  const [roles, setRoles] = useState(['admin', 'bruger']);
  const [permissions, setPermissions] = useState({});
  const [initialPermissions, setInitialPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const token = (() => {
    try { return localStorage.getItem('token'); } catch (_) { return null; }
  })();

  useEffect(() => {
    const fetchAccess = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch('/api/indstillinger/access-control', {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Kunne ikke hente adgangsrettigheder');
        }
        const data = await res.json();
        setPages(Array.isArray(data.pages) && data.pages.length ? data.pages : PAGE_DEFINITIONS);
        setRoles(Array.isArray(data.roles) && data.roles.length ? data.roles : ['admin', 'bruger']);
        setPermissions(data.permissions || {});
        setInitialPermissions(data.permissions || {});
      } catch (err) {
        setError(err.message || 'Kunne ikke hente adgangsrettigheder');
      } finally {
        setLoading(false);
      }
    };

    fetchAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const serializePermissions = (perm) => {
    const normalized = {};
    pages.forEach(page => {
      const values = perm[page.id] || [];
      normalized[page.id] = [...values].sort();
    });
    return JSON.stringify(normalized);
  };

  const hasChanges = useMemo(() => {
    return serializePermissions(permissions) !== serializePermissions(initialPermissions);
  }, [permissions, initialPermissions, pages]);

  const togglePermission = (pageId, role) => {
    setPermissions(prev => {
      const current = new Set(prev[pageId] || []);
      if (pageId === 'indstillinger' && role === 'admin') {
        return prev;
      }
      if (current.has(role)) {
        current.delete(role);
      } else {
        current.add(role);
      }
      return {
        ...prev,
        [pageId]: Array.from(current)
      };
    });
    setError('');
    setMessage('');
  };

  const handleReset = () => {
    setPermissions(initialPermissions);
    setMessage('');
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const res = await fetch('/api/indstillinger/access-control', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ permissions })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Kunne ikke gemme adgangsrettigheder');
      }
      const data = await res.json();
      setPermissions(data.permissions || permissions);
      setInitialPermissions(data.permissions || permissions);
      setMessage('Adgangsrettigheder gemt.');
    } catch (err) {
      setError(err.message || 'Kunne ikke gemme adgangsrettigheder');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Adgangsrettigheder</h2>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          {'V' + '\u00E6' + 'lg hvilke roller der har adgang til de enkelte sider. ' + '\u00C6' + 'ndringerne p' + '\u00E5' + 'virker navigationsmenu og direkte sideadgang.'}
        </p>
      </div>

      {(message || error) && (
        <div className={`p-4 rounded-lg border ${
          error
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-green-50 border-green-200 text-green-700'
        }`}>
          {error || message}
        </div>
      )}

      <div className="overflow-x-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                Side
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                Beskrivelse
              </th>
              {roles.map(role => (
                <th key={role} className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                  {role}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
            {pages.map((page) => {
              const activeRoles = new Set(permissions[page.id] || []);
              return (
                <tr key={page.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {page.label}
                  </td>
                  <td className="px-6 py-4 whitespace-normal text-sm text-gray-600 dark:text-gray-300">
                    {page.description}
                  </td>
                  {roles.map(role => {
                    const checked = activeRoles.has(role);
                    const disabled = page.id === 'indstillinger' && role === 'admin';
                    return (
                      <td key={`${page.id}-${role}`} className="px-6 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={checked || disabled}
                          onChange={() => togglePermission(page.id, role)}
                          disabled={disabled || saving}
                          className="h-4 w-4 text-falck-blue focus:ring-falck-blue border-gray-300 rounded"
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-3 justify-end">
        <button
          type="button"
          onClick={handleReset}
          disabled={!hasChanges || saving}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border transition-colors ${
            hasChanges && !saving
              ? 'border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800'
              : 'border-gray-200 text-gray-400 cursor-not-allowed dark:border-gray-800 dark:text-gray-500'
          }`}
        >
          <FaRedoAlt /> {'Nulstil ' + '\u00E6' + 'ndringer'}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-white transition-colors ${
            hasChanges && !saving
              ? 'bg-falck-blue hover:bg-blue-700'
              : 'bg-blue-300 cursor-not-allowed'
          }`}
        >
          <FaSave /> {saving ? 'Gemmer...' : 'Gem ' + '\u00E6' + 'ndringer'}
        </button>
      </div>
    </div>
  );
};

export default IndstillingerAdgang;
